import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTopicsStore, useCardsStore, useIndexCardsStore } from '@/stores'
import { ContributionHeatmap } from '@/components/ContributionHeatmap'
import '@/components/IndexCard/IndexCard.css'
import './DashboardPage.css'

export default function DashboardPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const { getTopicsSorted, addTopic, totalCards, isLoading: topicsLoading } = useTopicsStore()
  const { cards, isLoading: cardsLoading } = useCardsStore()
  const { getIndexCardCount } = useIndexCardsStore()
  const sortedTopics = getTopicsSorted()
  const indexCardCount = getIndexCardCount()

  const isLoading = topicsLoading || cardsLoading

  const [showNewTopic, setShowNewTopic] = useState(false)
  const [newTopicId, setNewTopicId] = useState('')
  const [newTopicName, setNewTopicName] = useState('')
  const [newTopicDesc, setNewTopicDesc] = useState('')

  // Reset form when navigating to dashboard via brand click
  useEffect(() => {
    if (location.state?.reset) {
      setShowNewTopic(false)
      setNewTopicId('')
      setNewTopicName('')
      setNewTopicDesc('')
    }
  }, [location.state])

  const getCardsCountForTopic = (topicId: string) => {
    return Array.from(cards.values()).filter((c) => c.topicId === topicId).length
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleCreateTopic = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newTopicId || !newTopicName) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await addTopic({
        id: newTopicId,
        name: newTopicName,
        description: newTopicDesc || undefined,
      })

      setNewTopicId('')
      setNewTopicName('')
      setNewTopicDesc('')
      setShowNewTopic(false)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p className="text-secondary">{t('dashboard.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewTopic(true)}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t('dashboard.newTopic')}
        </button>
      </div>

      {!isLoading && sortedTopics.length > 0 && (
        <>
          <div className="dashboard-stats">
            <div className="stat-item">
              <span className="stat-value">{sortedTopics.length}</span>
              <span className="stat-label">{t('dashboard.stats.topics')}</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{totalCards}</span>
              <span className="stat-label">{t('dashboard.stats.totalCards')}</span>
            </div>
          </div>

          <ContributionHeatmap cards={Array.from(cards.values())} />
        </>
      )}

      {showNewTopic && (
        <div className="new-topic-form card">
          <h3>{t('topic.create.title')}</h3>
          <form onSubmit={handleCreateTopic}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="topicId">{t('topic.create.idLabel')}</label>
                <input
                  type="text"
                  id="topicId"
                  className="input"
                  placeholder={t('topic.create.idPlaceholder')}
                  value={newTopicId}
                  onChange={(e) => setNewTopicId(e.target.value.replace(/\D/g, ''))}
                  required
                />
                <small className="form-hint">{t('topic.create.idHint')}</small>
              </div>
              <div className="form-group flex-1">
                <label htmlFor="topicName">{t('topic.create.nameLabel')}</label>
                <input
                  type="text"
                  id="topicName"
                  className="input"
                  placeholder={t('topic.create.namePlaceholder')}
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  onInput={(e) => {
                    // Workaround for IME input scroll issue on Linux
                    const input = e.target as HTMLInputElement
                    const pos = input.selectionStart
                    if (pos !== null) {
                      requestAnimationFrame(() => {
                        input.setSelectionRange(pos, pos)
                      })
                    }
                  }}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="topicDesc">{t('topic.create.descLabel')}</label>
              <textarea
                id="topicDesc"
                className="input"
                placeholder={t('topic.create.descPlaceholder')}
                value={newTopicDesc}
                onChange={(e) => setNewTopicDesc(e.target.value)}
                rows={2}
              />
            </div>
            {submitError && (
              <div className="form-error">{submitError}</div>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowNewTopic(false)} disabled={isSubmitting}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? t('common.loading') : t('dashboard.createTopic')}
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>{t('common.loading')}</p>
        </div>
      ) : sortedTopics.length === 0 ? (
        <div className="empty-state">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
          </svg>
          <h3>{t('dashboard.noTopics')}</h3>
          <p>{t('dashboard.noTopicsDesc')}</p>
          <button className="btn btn-primary" onClick={() => setShowNewTopic(true)}>
            {t('dashboard.createTopic')}
          </button>
        </div>
      ) : (
        <>
          {/* Index Entry Point */}
          <Link to="/index" className="index-entry-card card card-clickable">
            <div className="index-entry-link">
              <div className="index-entry-header">
                <span className="topic-id-badge topic-id-badge--index">17/</span>
                <h3>{t('index.title')}</h3>
                <span className="index-badge">
                  {indexCardCount} {t('index.entries')}
                </span>
              </div>
              <p className="index-entry-description">
                {t('index.dashboardDescription')}
              </p>
            </div>
          </Link>

          {/* Topics Grid */}
          <div className="topics-grid">
            {sortedTopics.map((topic) => (
              <Link key={topic.id} to={`/topic/${topic.id}`} className="topic-card card card-clickable">
                <div className="topic-card-header">
                  <span className="topic-id-badge">{topic.id}/</span>
                  <h3 className="topic-name">{topic.name}</h3>
                </div>
                {topic.description && (
                  <p className="topic-description">{topic.description}</p>
                )}
                <div className="topic-card-footer">
                  <span className="cards-count">
                    {getCardsCountForTopic(topic.id)} {t('topic.cards')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
